/* assets/warranty.js
 * --------------------------------------------------------------
 * 1) Pinta las opciones de garantía según el tramo de precio de la
 *    variante seleccionada del producto.
 * 2) Al añadir al carrito, añade producto + garantía enlazados con
 *    line item properties (_warranty_ref / _covers).
 * 3) En todo el sitio, mantiene el carrito coherente: si se elimina
 *    el producto, elimina su garantía huérfana y ajusta cantidades.
 *
 * Incluir en theme.liquid:
 *   <script src="{{ 'warranty.js' | asset_url }}" defer></script>
 * -------------------------------------------------------------- */

(() => {
  'use strict';

  /* ============================================================
     Constantes
  ============================================================ */

  const SYNC_DEBOUNCE_MS = 400;
  const VARIANT_CHANGE_DELAY_MS = 50;
  const CART_MUTATION_RE = /\/cart\/(change|update|add|clear)/;

  // Card seleccionable, mismo patrón tw/daisyUI que el formulario de reparaciones.
  // Mantener en sincronía con la opción "Sin garantía" de snippets/warranty-selector.liquid.
  const OPTION_LABEL_CLASSES =
    'tw:group tw:relative tw:flex! tw:items-center tw:gap-3 tw:p-4 tw:m-0 ' +
    'tw:bg-[color-mix(in_oklab,var(--color-base-200)_3%,transparent)] ' +
    'tw:border-2 tw:border-[color-mix(in_oklab,var(--color-base-200)_15%,transparent)] ' +
    'tw:rounded-xl tw:cursor-pointer tw:transition-all tw:duration-200 ' +
    'tw:hover:border-[color-mix(in_oklab,var(--color-primary)_80%,transparent)] ' +
    'tw:has-checked:border-primary ' +
    'tw:has-checked:bg-[color-mix(in_oklab,var(--color-primary)_5%,transparent)] ' +
    'tw:has-checked:ring-2 ' +
    'tw:has-checked:ring-[color-mix(in_oklab,var(--color-primary)_20%,transparent)]';

  const OPTION_RADIO_CLASSES =
    'tw:w-5 tw:h-5 tw:shrink-0 tw:rounded-full tw:border-2 tw:border-gray-300 ' +
    'tw:flex tw:items-center tw:justify-center tw:transition-colors tw:duration-200 ' +
    'tw:group-has-checked:border-primary';

  const OPTION_DOT_CLASSES =
    'tw:w-2 tw:h-2 tw:rounded-full tw:bg-primary tw:opacity-0 ' +
    'tw:transition-opacity tw:duration-200 tw:group-has-checked:opacity-100';

  /* ============================================================
     Utilidades
  ============================================================ */

  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return format ? format.replace(/\{\{\s*amount[^}]*\}\}/, value) : `${value} €`;
  };

  // Un tramo "300-600" cubre precios en céntimos [30000, 60000).
  // "1200+" cubre desde 120000 en adelante.
  const tierMatches = (tier, priceCents) => {
    const t = tier.trim();
    if (t.endsWith('+')) return priceCents >= parseFloat(t) * 100;

    const [min, max] = t.split('-').map((n) => parseFloat(n) * 100);
    return priceCents >= min && priceCents < max;
  };

  const uid = () => `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  /* ============================================================
     1. Selector de garantía en la ficha de producto
  ============================================================ */

  const initProductSelector = () => {
    const dataEl = document.getElementById('warranty-data');
    const selectorEl = document.getElementById('warranty-selector');
    if (!dataEl || !selectorEl) return;

    const data = JSON.parse(dataEl.textContent);
    const optionsEl = document.getElementById('warranty-selector-options');
    const descriptionEl = document.getElementById('warranty-selector-description');
    const errorEl = document.getElementById('warranty-selector-error');
    const productForm =
      selectorEl.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    /* ---------- Estado de la variante seleccionada ---------- */

    // Sin el guardia, un <select> sin valor daría NaN -> JSON.stringify lo manda
    // como null y /cart/add.js responde "missing or invalid: items".
    const currentVariantId = () => {
      const input = productForm?.querySelector('[name="id"]');
      const parsed = parseInt(input?.value, 10);
      return Number.isFinite(parsed) ? parsed : data.productVariants[0].id;
    };

    const currentVariant = () => {
      const id = currentVariantId();
      return data.productVariants.find((v) => v.id === id) ?? data.productVariants[0];
    };

    const selectedWarrantyId = () => {
      const chosen = selectorEl.querySelector('input[name="warranty_choice"]:checked');
      return chosen?.value ? parseInt(chosen.value, 10) : null;
    };

    /* ---------- Render ---------- */

    // Muestra la descripción de la garantía seleccionada (metafield custom.descripcion).
    // Llega como HTML ya renderizado por Liquid (metafield_tag) — contenido del
    // propio comercio, seguro para innerHTML.
    const updateDescription = (warrantyId) => {
      if (!descriptionEl) return;

      const warranty = warrantyId
        ? data.warrantyVariants.find((v) => v.id === warrantyId)
        : null;
      const html = warranty?.description || '';

      descriptionEl.innerHTML = html;
      descriptionEl.hidden = !html;
    };

    // Error inline: el detalle va a consola, al cliente solo el mensaje corto.
    const showError = (detail) => {
      console.error('[warranty] no se pudo añadir al carrito:', detail);
      if (!errorEl) return;
      errorEl.textContent = 'No se pudo añadir al carrito. Inténtalo de nuevo.';
      errorEl.hidden = false;
    };

    const clearError = () => {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    };

    const renderOptions = () => {
      const variant = currentVariant();
      const applicable = data.warrantyVariants.filter(
        (w) => w.available && tierMatches(w.tier, variant.price)
      );

      optionsEl.innerHTML = applicable
        .map(
          (w) => `
            <label class="${OPTION_LABEL_CLASSES}">
              <input type="radio" name="warranty_choice" value="${w.id}" class="tw:sr-only">
              <span class="${OPTION_RADIO_CLASSES}">
                <span class="${OPTION_DOT_CLASSES}"></span>
              </span>
              <span class="tw:text-sm tw:font-medium tw:text-gray-900">${w.duration} de garantía adicional</span>
              <span class="tw:ml-auto tw:text-sm tw:font-semibold tw:text-gray-900">+${formatMoney(w.price, data.moneyFormat)}</span>
            </label>`
        )
        .join('');

      // Ocultar el bloque si el producto no tiene tramo aplicable (p. ej. accesorios baratos)
      selectorEl.style.display = applicable.length ? '' : 'none';

      // Al re-pintar (p. ej. cambio de variante), volver a "Sin garantía" y ocultar descripción
      const noneRadio = selectorEl.querySelector('input[name="warranty_choice"][value=""]');
      if (noneRadio) noneRadio.checked = true;
      updateDescription(null);
      clearError();
    };

    /* ---------- Añadir al carrito (producto + garantía enlazados) ---------- */

    const addToCartWithWarranty = async (warrantyId) => {
      const ref = uid();
      const qty = parseInt(productForm.querySelector('[name="quantity"]')?.value, 10) || 1;
      const productVariantId = currentVariantId();

      // Cortar aquí: un id no numérico solo produce un 422 genérico.
      if (!Number.isFinite(productVariantId) || !Number.isFinite(warrantyId)) {
        throw new Error(`ids no válidos (producto: ${productVariantId}, garantía: ${warrantyId})`);
      }

      const payload = {
        items: [
          {
            id: productVariantId,
            quantity: qty,
            properties: { _warranty_ref: ref },
          },
          {
            id: warrantyId,
            quantity: qty,
            properties: {
              _covers: ref,
              Cubre: data.productTitle,
            },
          },
        ],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`/cart/add.js respondió ${response.status}`);

      // Refrescar el drawer del carrito si el tema lo soporta; si no, ir al carrito
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const drawer = document.querySelector('cart-drawer, #CartDrawer');
      if (drawer) window.location.reload();
      else window.location.href = '/cart';
    };

    /* ---------- Listeners ---------- */

    // Delegado: funciona aunque las opciones se re-pinten
    selectorEl.addEventListener('change', (e) => {
      if (e.target?.name === 'warranty_choice') {
        updateDescription(e.target.value ? parseInt(e.target.value, 10) : null);
      }
    });

    // Re-render al cambiar de variante (cubre selects nativos y la mayoría de temas OS 2.0).
    // Pequeño delay para que el tema actualice antes el input [name="id"].
    const scheduleRender = () => setTimeout(renderOptions, VARIANT_CHANGE_DELAY_MS);

    productForm?.addEventListener('change', (e) => {
      if (
        e.target.name === 'id' ||
        e.target.closest('variant-selects, variant-radios, .product-form__input')
      ) {
        scheduleRender();
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target?.name === 'id') scheduleRender();
    });

    // Interceptar el envío del formulario para añadir ambas líneas enlazadas.
    // capture: nos adelantamos al handler AJAX del tema.
    productForm?.addEventListener(
      'submit',
      (e) => {
        const warrantyId = selectedWarrantyId();
        if (!warrantyId) return; // sin garantía: comportamiento normal del tema

        e.preventDefault();
        e.stopImmediatePropagation();
        clearError();

        addToCartWithWarranty(warrantyId).catch(showError);
      },
      true
    );

    renderOptions();
  };

  /* ============================================================
     2. Sincronización del carrito (todo el sitio)
        - Garantía huérfana (sin su producto) -> se elimina.
        - Cantidad distinta a la del producto -> se iguala.
  ============================================================ */

  const syncCart = async () => {
    try {
      const cart = await (await fetch('/cart.js')).json();

      const productsByRef = new Map(); // _warranty_ref -> line item del producto
      const warranties = [];

      for (const item of cart.items) {
        const props = item.properties ?? {};
        if (props._warranty_ref) productsByRef.set(props._warranty_ref, item);
        if (props._covers) warranties.push(item);
      }

      const updates = {};
      for (const w of warranties) {
        const owner = productsByRef.get(w.properties._covers);
        if (!owner) updates[w.key] = 0;
        else if (w.quantity !== owner.quantity) updates[w.key] = owner.quantity;
      }

      if (!Object.keys(updates).length) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (window.location.pathname === '/cart') window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {
      /* silencioso: un fallo de sync no debe romper la navegación */
    }
  };

  // Envuelve fetch para detectar mutaciones AJAX del carrito y programar
  // una sincronización, con debounce para agrupar ráfagas de peticiones.
  const watchCartMutations = () => {
    const originalFetch = window.fetch;
    let syncScheduled = false;

    window.fetch = function (...args) {
      const url = args[0]?.toString() ?? '';
      const result = originalFetch.apply(this, args);

      if (CART_MUTATION_RE.test(url) && !url.includes('warranty-sync')) {
        result.finally(() => {
          if (syncScheduled) return;
          syncScheduled = true;
          setTimeout(() => {
            syncScheduled = false;
            syncCart();
          }, SYNC_DEBOUNCE_MS);
        });
      }

      return result;
    };
  };

  /* ============================================================
     Init
  ============================================================ */

  initProductSelector();
  document.addEventListener('DOMContentLoaded', syncCart);
  watchCartMutations();
})();
